import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const StudentDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState({
    profile: null,
    attendance: [],
    results: [],
    notices: []
  });

  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch student profile
        const profileResponse = await api.get(`/students/user/${user?._id}`);
        
        // Fetch student attendance
        const attendanceResponse = await api.get(`/attendance/student/${profileResponse.data.data._id}`);
        
        // Fetch student results
        const resultsResponse = await api.get(`/results/student/${profileResponse.data.data._id}`);
        
        // Fetch recent notices
        const noticesResponse = await api.get('/notices');
        
        setDashboardData({
          profile: profileResponse.data.data,
          attendance: attendanceResponse.data.data.slice(0, 5),
          results: resultsResponse.data.data.slice(0, 3),
          notices: noticesResponse.data.data.slice(0, 5)
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Fallback to mock data if API fails
        setDashboardData({
          profile: {
            name: user?.name || 'John Doe',
            roll: 'STU001',
            class: 'Grade 10',
            section: 'A',
            session: '2023-2024',
            email: user?.email || 'student@example.com',
            phone: '+1234567890',
            guardianName: 'Mr. John Parent',
            guardianPhone: '+1234567891'
          },
          attendance: [
            { date: '2023-12-01', status: 'Present' },
            { date: '2023-12-02', status: 'Present' },
            { date: '2023-12-03', status: 'Absent' },
            { date: '2023-12-04', status: 'Present' },
            { date: '2023-12-05', status: 'Present' },
          ],
          results: [
            { exam: 'Mid Term', class: 'Grade 10', subject: 'Mathematics', marks: 85, grade: 'A', date: '2023-11-15' },
            { exam: 'Mid Term', class: 'Grade 10', subject: 'Science', marks: 92, grade: 'A+', date: '2023-11-16' },
            { exam: 'Unit Test', class: 'Grade 10', subject: 'English', marks: 78, grade: 'B+', date: '2023-10-20' }
          ],
          notices: [
            { id: 1, title: 'Holiday Notice', date: '2023-12-10', priority: 'Important' },
            { id: 2, title: 'Exam Schedule', date: '2023-12-05', priority: 'Urgent' }
          ]
        });
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const presentCount = dashboardData.attendance.filter(att => att.status === 'Present').length;
  const totalCount = dashboardData.attendance.length;
  const attendancePercentage = totalCount > 0 ? Math.round((presentCount / totalCount) * 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Student Dashboard</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Profile and Quick Actions */}
          <div className="lg:col-span-1 space-y-6">
            {/* Profile Card */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">My Profile</h2>
              <div className="flex items-center mb-4">
                <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-800">{dashboardData.profile?.name}</h3>
                  <p className="text-gray-600">Roll: {dashboardData.profile?.roll}</p>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Class:</span> {dashboardData.profile?.class}</p>
                <p><span className="font-medium">Section:</span> {dashboardData.profile?.section}</p>
                <p><span className="font-medium">Session:</span> {dashboardData.profile?.session}</p>
                <p><span className="font-medium">Email:</span> {dashboardData.profile?.email}</p>
                <p><span className="font-medium">Phone:</span> {dashboardData.profile?.phone}</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <a href="/student/profile" className="block w-full text-left bg-primary text-white px-4 py-2 rounded hover:bg-blue-700 transition">
                  View Profile
                </a>
                <a href="/student/attendance" className="block w-full text-left bg-gray-100 text-gray-800 px-4 py-2 rounded hover:bg-gray-200 transition">
                  View Attendance
                </a>
                <a href="/student/results" className="block w-full text-left bg-gray-100 text-gray-800 px-4 py-2 rounded hover:bg-gray-200 transition">
                  View Results
                </a>
                <a href="/student/admit-card" className="block w-full text-left bg-gray-100 text-gray-800 px-4 py-2 rounded hover:bg-gray-200 transition">
                  Download Admit Card
                </a>
                <a href="/student/change-password" className="block w-full text-left bg-gray-100 text-gray-800 px-4 py-2 rounded hover:bg-gray-200 transition">
                  Change Password
                </a>
              </div>
            </div>
          </div>

          {/* Right Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Attendance Summary */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Attendance Summary</h2>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-3xl font-bold text-primary">{attendancePercentage}%</p>
                  <p className="text-gray-600">Attendance Rate</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold">{presentCount}/{totalCount} Days</p>
                  <p className="text-gray-600">Present/Days</p>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-primary h-2.5 rounded-full" 
                  style={{ width: `${attendancePercentage}%` }}
                ></div>
              </div>
            </div>

            {/* Recent Results */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Recent Results</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exam</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Marks</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dashboardData.results.slice(0, 3).map((result, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{result.exam}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{result.subject}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{result.marks}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            result.grade === 'A+' || result.grade === 'A' ? 'bg-green-100 text-green-800' :
                            result.grade === 'B+' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {result.grade}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{result.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recent Notices */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Recent Notices</h2>
              <div className="space-y-4">
                {dashboardData.notices.map(notice => (
                  <div key={notice.id} className="border-l-4 border-primary pl-4 py-2">
                    <h3 className="font-medium text-gray-800">{notice.title}</h3>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-sm text-gray-600">{notice.date}</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        notice.priority === 'Urgent' ? 'bg-red-100 text-red-800' :
                        notice.priority === 'Important' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {notice.priority}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
